import { FC, useCallback, useEffect, useRef, useState, ChangeEvent } from 'react';
import { getTrackBackground, Range } from 'react-range';
// import CloseIcon from '/public/icons/close.svg';
import { useFiltersStore } from '@/shared/store/filtersStore';
import { cvaInput, cvaInputs, cvaInputWrap, cvaRange, cvaRangeThumb, cvaRangeTrack, cvaRoot } from './RangeItemStyles';

const getClearNum = (str: string) => str.replace(/[^+\d]/g, '');

interface Props {
  from: number;
  to: number;
  min: number;
  max: number;
  onChange: (values: number[]) => void;
  onReset: () => void;
}

const getValueFrom = (str: string, values: number[], max: number, min: number) => {
  const num = +getClearNum(str) || 0;
  const currentMax = Math.min(max, values[1]);
  const from = num > currentMax ? currentMax : num < min ? min : num;

  return from;
};

const getValueTo = (str: string, values: number[], max: number, min: number) => {
  const num = +getClearNum(str) || 0;
  if (num === 0) {
    return max;
  }

  const currentMin = Math.max(min, values[0]);
  const to = num < currentMin ? currentMin : num > max ? max : num;

  return to;
};

const RangeItem: FC<Props> = ({ onChange, from, to, min, max }) => {
  const inputFromRef = useRef<HTMLInputElement>(null);
  const inputToRef = useRef<HTMLInputElement>(null);
  const [values, setValues] = useState<number[]>([from, to]);
  const [valuesLabel, setValuesLabel] = useState<string[]>([`${from}`, `${to}`]);

  const [isFocusFrom, toggleFocusFrom] = useState(false);
  const [isFocusTo, toggleFocusTo] = useState(false);

  const filtersRangeState = useFiltersStore((state) => state.rangeFilters);

  useEffect(() => {
    if (!filtersRangeState?.PRICE) {
      setValues([from, to]);
      setValuesLabel([`${from}`, `${to}`]);
    }
  }, [filtersRangeState]);

  const onChangeFrom = (e: ChangeEvent<HTMLInputElement>) => {
    const str = getClearNum(e.target.value);

    setValuesLabel((prev) => [str, prev[1]]);
  };

  const onChangeTo = (e: ChangeEvent<HTMLInputElement>) => {
    const str = getClearNum(e.target.value);

    setValuesLabel((prev) => [prev[0], str]);
  };

  const onChangeRange = useCallback((values: number[]) => {
    setValues(values);
    setValuesLabel([`${values[0]}`, `${values[1]}`]);
  }, []);
  const onFinalChangeRange = useCallback(
    (values: number[]) => {
      setValues(values);
      setValuesLabel([`${values[0]}`, `${values[1]}`]);
      onChange(values);
    },
    [onChange]
  );

  const onSubmit = useCallback(
    (e?: any) => {
      e?.preventDefault();
      const valueFrom = inputFromRef?.current?.value || '';
      const valueTo = inputToRef?.current?.value || '';
      const newFrom = getValueFrom(valueFrom, values, max, min);
      const newTo = getValueTo(valueTo, values, max, min);
      const newValues = [newFrom, newTo];

      setValues(newValues);
      setValuesLabel([`${newFrom}`, `${newTo}`]);
      onChange(newValues);
    },
    [values, max, min, onChange]
  );

  const onFocusTo = useCallback(() => toggleFocusTo(true), []);
  const onFocusFrom = useCallback(() => toggleFocusFrom(true), []);

  const onBlurFrom = () => {
    toggleFocusFrom(false);
    onSubmit();
  };
  const onBlurTo = () => {
    toggleFocusTo(false);
    onSubmit();
  };

  return (
    <div className={cvaRoot()} data-body-scroll-lock-ignore={'true'}>
      <div className={cvaInputs()}>
        <div className={cvaInputWrap()}>
          <input
            ref={inputFromRef}
            className={cvaInput()}
            type="text"
            onChange={onChangeFrom}
            value={!isFocusFrom ? `от ${valuesLabel[0]}` : valuesLabel[0]}
            onFocus={onFocusFrom}
            onBlur={onBlurFrom}
            pattern="[0-9]*"
            inputMode="numeric"
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
          {/* <CloseIcon
            className={cvaInputClear()}
            onClick={() => {
              setValues((prev) => {
                return [Number(min), prev[1]];
              });
            }}
          /> */}
          {/* <span className={cvaNumberValue()}>{ focusFrom ? price.from : formatCurrency(price.from, true) }</span> */}
        </div>
        <div className={cvaInputWrap()}>
          <input
            ref={inputToRef}
            className={cvaInput({ last: true })}
            type="text"
            onChange={onChangeTo}
            onFocus={onFocusTo}
            onBlur={onBlurTo}
            pattern="[0-9]*"
            inputMode="numeric"
            value={!isFocusTo ? `от ${valuesLabel[1]}` : valuesLabel[1]}
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
          {/* <CloseIcon
            className={cvaInputClear()}
            onClick={() => {
              setValues((prev) => {
                return [prev[0], Number(max)];
              });
            }}
          /> */}
          {/* <span className={cvaNumberValue()}>{ focusTo ? price.to : formatCurrency(price.to, true) }</span> */}
        </div>
      </div>
      <div className={cvaRange()}>
        <Range
          step={1}
          min={min === max ? max - 1 : min}
          max={max}
          values={values}
          onChange={onChangeRange}
          onFinalChange={onFinalChangeRange}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              style={{
                ...props.style,
                background: getTrackBackground({
                  values: values,
                  colors: ['rgba(232, 232, 232, 1)', 'rgba(61, 200, 105, 1)', 'rgba(232, 232, 232, 1)'],
                  min: min,
                  max: max,
                }),
              }}
              className={cvaRangeTrack()}
            >
              {children}
            </div>
          )}
          renderThumb={({ props: { key, ...props } }) => (
            <div
              key={key}
              {...props}
              style={{
                ...props.style,
              }}
              className={cvaRangeThumb()}
            ></div>
          )}
        />
      </div>
    </div>
  );
};

export default RangeItem;
